import React from "react";
import { useParams } from "react-router-dom";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";
import ViewPage from "../../components/view/ViewPage";
import ViewFieldCard from "../../components/view/fields/ViewFieldCard";
import ViewRichTextField from "../../components/view/fields/ViewRichTextField";
import ViewSubCard from "../../components/view/fields/ViewSubCard";
import ViewTextField from "../../components/view/fields/ViewTextField";
import ViewVersionReferenceField from "../../components/view/fields/ViewVersionReferenceField";
import { useEntityHandlerContext } from "../../controllers/entityController";
import useColophonController from "../../controllers/colophonController";
import { Colophon } from "../../types/colophon";
import { NameInternalEvidence } from "../../types/nameEvidence";
import { VersionReference } from "../../types/version";

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth = false }) => (
  <Grid size={fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography variant="body1">{value}</Typography>
      ) : (
        value
      )}
    </Box>
  </Grid>
);

const SubCardDetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth = false }) => (
  <Grid size={fullWidth ? { xs: 12 } : { xs: 12, sm: 6 }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Box>
  </Grid>
);

const EmptyValue = () => <Typography variant="body1">N/A</Typography>;

const ColophonReferencesContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Colophon>();
  const colophon = methods.getValues();

  return (
    <>
      <DetailField
        label="Work"
        value={
          colophon.work ? (
            <EntityReferenceComponent entity={colophon.work} linkMode="view" />
          ) : (
            <EmptyValue />
          )
        }
      />
      <DetailField
        label="Author of Colophon"
        value={
          colophon.author ? (
            <EntityReferenceComponent entity={colophon.author} linkMode="view" />
          ) : (
            <EmptyValue />
          )
        }
      />
    </>
  );
};

const NameInternalEvidenceSubCard: React.FC<{
  evidence?: NameInternalEvidence | null;
}> = ({ evidence }) => (
  <ViewSubCard variant="nested" title="Name Internal Evidence">
    <SubCardDetailRow
      label="Attribute Pre"
      value={displayText(evidence?.attribute_pre)}
    />
    <SubCardDetailRow label="Name" value={displayText(evidence?.name)} />
    <SubCardDetailRow
      label="Attribute Post"
      value={displayText(evidence?.attribute_post)}
    />
    <SubCardDetailRow
      label="Internal Evidence Type"
      value={displayText(evidence?.internal_evidence_type)}
    />
  </ViewSubCard>
);

const NameInternalEvidenceContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Colophon>();
  const colophon = methods.getValues();

  return <NameInternalEvidenceSubCard evidence={colophon.name_internal_evidence} />;
};

const getVersionExistsLabel = (version?: VersionReference | null): string => {
  if (!version || typeof version.version_exist !== "boolean") {
    return "N/A";
  }

  return version.version_exist ? "Yes" : "No";
};

const VersionReferenceSubCard: React.FC<{
  version?: VersionReference | null;
}> = ({ version }) => {
  const showVersion = version?.version_exist === true;
  const showVersionNotes = version?.version_exist === false;

  return (
    <ViewSubCard variant="nested" title="Version Reference">
      <SubCardDetailRow
        label="Version Exists"
        value={getVersionExistsLabel(version)}
      />
      {showVersion ? (
        <SubCardDetailRow
          label="Version"
          value={<ViewVersionReferenceField value={version} />}
          fullWidth
        />
      ) : null}
      {showVersionNotes ? (
        <SubCardDetailRow
          label="Version Notes"
          value={<ViewVersionReferenceField value={version} />}
          fullWidth
        />
      ) : null}
    </ViewSubCard>
  );
};

const VersionReferenceContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Colophon>();
  const colophon = methods.getValues();

  return <VersionReferenceSubCard version={colophon.version} />;
};

const VirtuallyIdenticalWithContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Colophon>();
  const colophon = methods.getValues();
  const versions = colophon.virtually_identical_with ?? [];

  if (versions.length === 0) {
    return <DetailField label="Versions" value="N/A" fullWidth />;
  }

  return (
    <>
      {versions.map((item, index) => (
        <ViewSubCard key={item.id || index} title={`Version ${index + 1}`}>
          <DetailField
            label="Version"
            value={
              item.version ? (
                <EntityReferenceComponent entity={item.version} linkMode="view" />
              ) : (
                displayText(item.id)
              )
            }
            fullWidth
          />
        </ViewSubCard>
      ))}
    </>
  );
};

const ViewColophonPage: React.FC = () => {
  const { _id } = useParams<{ _id: string }>();

  if (!_id) {
    throw new Error("Colophon ID is required for viewing.");
  }

  const controller = useColophonController(_id);

  return (
    <ViewPage<Colophon> controller={controller}>
      <ViewFieldCard title="Basic Information">
        <ViewTextField name="published_id" label="Intellexus ID" />
      </ViewFieldCard>

      <ViewFieldCard title="Linked References">
        <ColophonReferencesContent />
      </ViewFieldCard>

      <ViewFieldCard title="Colophon">
        <ViewTextField name="colophon" label="Colophon" />
      </ViewFieldCard>

      <ViewFieldCard title="Name Internal Evidence">
        <NameInternalEvidenceContent />
      </ViewFieldCard>

      <ViewFieldCard title="Version">
        <VersionReferenceContent />
      </ViewFieldCard>

      <ViewFieldCard title="Virtually Identical With">
        <VirtuallyIdenticalWithContent />
      </ViewFieldCard>

      <ViewFieldCard title="Notes">
        <ViewRichTextField name="notes" />
      </ViewFieldCard>
    </ViewPage>
  );
};

export default ViewColophonPage;
